import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, TrashSimple, MagnifyingGlass, Copy, FunnelSimple, Invoice, DownloadSimple, PencilSimple, Paperclip, Printer, CheckCircle, Check } from "@phosphor-icons/react";
import { Modal, ModalFooter, Fld, InvSuggestion, AmountSuggestion } from "./TCAndTCSR";
import { printFilterSummary } from "@/lib/printSummary";
import { MultiFilePicker } from "@/components/MultiFilePicker";
import { extractFromFile } from "@/lib/invoiceExtractor";

function fmtDate(s){ if(!s) return "—"; if(/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; const d=new Date(s); if(Number.isNaN(d.getTime())) return s; return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});}
function fmtRM(n){ const v = Number(n || 0); return `RM ${v.toLocaleString("en-MY",{minimumFractionDigits:2,maximumFractionDigits:2})}`;}

export default function PurchaseInv() {
  return <PurchaseInvPage
    variant="primary"
    title="Purchase INV"
    subtitle="Purchase invoices register. Attach any file — PDF, Excel, ZIP, CSV, Word, images — up to platform limits."
    extraHeaders={{}}
    passcodeQuery=""
    testIdPrefix="pi"
  />;
}

export function PurchaseInvPage({ variant, title, subtitle, extraHeaders, passcodeQuery, testIdPrefix }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const [q, setQ] = useState(params.get("q") || "");
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [sortKey, setSortKey] = useState(params.get("sort") || "date");
  const [sortDir, setSortDir] = useState(params.get("dir") === "asc" ? "asc" : "desc");

  useEffect(() => {
    const n = {};
    if (q) n.q = q; if (from) n.from = from; if (to) n.to = to;
    if (sortKey) { n.sort = sortKey; n.dir = sortDir; }
    setParams(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, from, to, sortKey, sortDir]);

  const cfg = { headers: extraHeaders };
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/purchase-invoices/${variant}`, cfg); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [variant, JSON.stringify(extraHeaders)]);

  const del = async (r) => {
    if (!window.confirm(`Delete invoice from ${r.company_name} (${r.inv_no || "no #"})?`)) return;
    try { await api.delete(`/purchase-invoices/${variant}/${r.id}`, cfg); setItems(xs => xs.filter(x => x.id !== r.id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const openFile = (id) => {
    const t = localStorage.getItem("access_token") || "";
    const pc = passcodeQuery ? `&passcode=${encodeURIComponent(passcodeQuery)}` : "";
    window.open(`${API}/purchase-invoices/${variant}/${id}/download?auth=${encodeURIComponent(t)}${pc}`, "_blank");
  };
  const shareLink = async () => {
    const url = window.location.href;
    try { await navigator.clipboard.writeText(url); toast.success("Filtered link copied"); return; } catch {}
    try {
      const ta = document.createElement("textarea"); ta.value = url; ta.style.position="fixed"; ta.style.opacity="0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      toast.success("Filtered link copied");
    } catch { toast.error("Could not copy — copy the URL manually"); }
  };
  const clearF = () => { setQ(""); setFrom(""); setTo(""); setSortKey("date"); setSortDir("desc"); };
  const toggleSort = (k) => {
    if (sortKey !== k) { setSortKey(k); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir("asc"); }
  };

  const togglePaid = async (r) => {
    let code = sessionStorage.getItem("pi2_passcode") || "";
    if (!code) {
      code = window.prompt("Enter passcode to change Paid status:") || "";
      if (!code) return;
    }
    try {
      const { data } = await api.patch(`/purchase-invoices/${variant}/${r.id}/paid`, { paid: !r.paid }, { headers: { "X-Passcode": code, ...extraHeaders }});
      sessionStorage.setItem("pi2_passcode", code);
      setItems(xs => xs.map(x => x.id === r.id ? { ...x, ...data } : x));
      toast.success(data.paid ? "Marked as paid" : "Marked as unpaid");
    } catch (e) {
      if (e?.response?.status === 403) {
        sessionStorage.removeItem("pi2_passcode");
        toast.error("Wrong passcode");
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  const filtered = useMemo(() => {
    const raw = q.trim();
    const n = raw.toLowerCase();
    // Exact-match status keywords: "paid" / "unpaid" filter by status only,
    // never as substrings of other fields.
    const statusOnly = n === "paid" ? true : n === "unpaid" ? false : null;
    return items.filter(x => {
      if (statusOnly !== null) {
        return Boolean(x.paid) === statusOnly;
      }
      if (n) { const hay = `${x.company_name||""} ${x.inv_no||""} ${x.description||""} ${x.filename||""}`.toLowerCase(); if (!hay.includes(n)) return false; }
      if (from && (x.date||"") && x.date < from) return false;
      if (to   && (x.date||"") && x.date > to)   return false;
      return true;
    });
  }, [items, q, from, to]);

  const displayed = useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    const g = (x) => ({
      date: x.date||"",
      company: (x.company_name||"").toLowerCase(),
      inv: (x.inv_no||"").toLowerCase(),
      description: (x.description||"").toLowerCase(),
      amount: Number(x.amount||0),
    }[sortKey] ?? "");
    return [...filtered].sort((a,b)=>{ const va=g(a), vb=g(b); if (va<vb) return -dir; if (va>vb) return dir; return 0; });
  }, [filtered, sortKey, sortDir]);

  const totalAmount = useMemo(() => displayed.reduce((s,x)=>s+Number(x.amount||0),0), [displayed]);
  const hasF = q || from || to;

  const onPrint = () => {
    printFilterSummary({
      title: `${title} — Filter Summary`,
      filters: [
        q && `Search: "${q}"`,
        from && `From: ${from}`,
        to && `To: ${to}`,
      ].filter(Boolean),
      columns: [
        { key: "date",         label: "Date",           format: fmtDate },
        { key: "company_name", label: "Company" },
        { key: "inv_no",       label: variant==="protected"?"Inv / Statement #":"Invoice #" },
        { key: "description",  label: "Description" },
        { key: "amount",       label: "Amount (RM)", align: "right", format: v => Number(v||0).toFixed(2) },
      ],
      rows: displayed,
      total: totalAmount,
      totalLabel: "Total Amount",
    });
  };

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Accounts</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">{title}</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onPrint} data-testid={`${testIdPrefix}-print-btn`} className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150"><Printer size={14}/> Print filter summary</button>
          <button onClick={shareLink} data-testid={`${testIdPrefix}-share-btn`} className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150"><Copy size={14}/> Copy share link</button>
          <button onClick={()=>setShowNew(true)} data-testid={`new-${testIdPrefix}-btn`} className="bg-[#002FA7] text-white text-sm px-4 py-2.5 flex items-center gap-2 hover:bg-[#00227A] transition-colors duration-150"><Plus size={16}/> New Invoice</button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 p-3 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid={`${testIdPrefix}-filters`}>
        <div className="md:col-span-2 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder='Search company, invoice #, description, file, "paid" or "unpaid"…' data-testid={`${testIdPrefix}-search`} className="w-full text-sm border border-zinc-200 pl-9 pr-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
        </div>
        <input type="date" value={from} onChange={e=>setFrom(e.target.value)} data-testid={`${testIdPrefix}-from`} className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
        <input type="date" value={to} onChange={e=>setTo(e.target.value)} data-testid={`${testIdPrefix}-to`} className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
      </div>

      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 bg-white border border-zinc-200 px-3 py-1.5 text-sm" data-testid={`${testIdPrefix}-total-pill`}>
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Filter total</span>
          <span className="font-display font-black text-zinc-950">{fmtRM(totalAmount)}</span>
          <span className="text-xs text-zinc-500 font-mono">· {displayed.length} item{displayed.length===1?"":"s"}</span>
        </div>
        {hasF && (
          <div className="text-xs text-zinc-500 font-mono flex items-center gap-3">
            <FunnelSimple size={12}/> Filtered from {items.length}
            <button onClick={clearF} className="text-[#002FA7] hover:underline">Clear filters</button>
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[1020px]">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500 bg-zinc-50">
              <SortTh label="Date"        k="date"        sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("date")}/>
              <SortTh label="Company"     k="company"     sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("company")}/>
              <SortTh label={variant==="protected"?"Inv / Statement #":"Invoice #"} k="inv" sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("inv")}/>
              <SortTh label="Description" k="description" sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("description")}/>
              <SortTh label="Amount"      k="amount"      align="right" sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("amount")}/>
              <th className="text-center px-4 py-3 whitespace-nowrap">Paid</th>
              <th className="text-left px-4 py-3">File</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-16 text-zinc-500 font-mono text-sm">Loading…</td></tr>
            : displayed.length === 0 ? <tr><td colSpan={8} className="text-center py-16 text-zinc-500"><Invoice size={40} weight="duotone" className="mx-auto text-zinc-300 mb-3"/><div className="font-mono text-sm">{items.length===0?"No invoices yet — add the first one.":"No invoices match these filters."}</div></td></tr>
            : displayed.map(r => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150" data-testid={`${testIdPrefix}-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-zinc-700 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-4 py-3 text-zinc-900 font-medium truncate max-w-[220px]">{r.company_name}</td>
                <td className="px-4 py-3 font-mono text-zinc-800">{r.inv_no || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3 text-zinc-700 truncate max-w-[280px]" title={r.description}>{r.description || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3 text-right font-mono text-zinc-900 whitespace-nowrap">{fmtRM(r.amount)}</td>
                <td className="px-4 py-3 text-center whitespace-nowrap">
                  <button type="button" onClick={()=>togglePaid(r)} data-testid={`${testIdPrefix}-paid-${r.id}`}
                    title={r.paid ? `Paid${r.paid_by_name?` by ${r.paid_by_name}`:""}${r.paid_at?` at ${r.paid_at.slice(0,10)}`:""}` : "Mark as paid (passcode required)"}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 border font-mono transition-colors duration-150 ${r.paid ? "bg-green-50 border-green-300 text-green-800 hover:bg-green-100" : "bg-white border-zinc-200 text-zinc-500 hover:bg-zinc-50"}`}>
                    <span className={`inline-block w-3 h-3 border flex items-center justify-center ${r.paid ? "bg-green-600 border-green-600 text-white" : "bg-white border-zinc-300"}`}>{r.paid && <Check size={9} weight="bold"/>}</span>
                    {r.paid ? "PAID" : "Unpaid"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  {r.filename ? (
                    <button onClick={()=>openFile(r.id)} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline text-sm" data-testid={`${testIdPrefix}-view-${r.id}`}>
                      <Paperclip size={14} weight="duotone"/> <span className="truncate max-w-[160px]">{r.filename}</span>
                    </button>
                  ) : <span className="text-zinc-400 font-mono text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {r.filename && (<button onClick={()=>openFile(r.id)} data-testid={`${testIdPrefix}-download-${r.id}`} className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150"><DownloadSimple size={14}/> File</button>)}
                  <button onClick={()=>setEditing(r)} data-testid={`${testIdPrefix}-edit-${r.id}`} className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150 ml-1"><PencilSimple size={14}/> Edit</button>
                  {(user?.role === "admin" || r.created_by_id === user?.id) && (<button onClick={()=>del(r)} data-testid={`${testIdPrefix}-delete-${r.id}`} className="inline-flex items-center gap-1 text-xs text-red-700 px-2 py-1 hover:bg-red-50 transition-colors duration-150 ml-1"><TrashSimple size={14}/> Delete</button>)}
                </td>
              </tr>
            ))}
          </tbody>
          {displayed.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-zinc-300 bg-zinc-50">
                <td colSpan={4} className="px-4 py-3 text-right text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-600">Total</td>
                <td className="px-4 py-3 text-right font-mono font-black text-zinc-950" data-testid={`${testIdPrefix}-footer-total`}>{fmtRM(totalAmount)}</td>
                <td colSpan={3}/>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showNew && <NewPIModal variant={variant} extraHeaders={extraHeaders} testIdPrefix={testIdPrefix} onClose={()=>setShowNew(false)} onCreated={rs=>{ setItems(xs=>[...rs,...xs]); setShowNew(false); }}/>}
      {editing && <EditPIModal variant={variant} extraHeaders={extraHeaders} testIdPrefix={testIdPrefix} record={editing} onClose={()=>setEditing(null)} onSaved={u=>{ setItems(xs=>xs.map(x=>x.id===u.id?{...x,...u}:x)); setEditing(null); }}/>}
    </Layout>
  );
}

function SortTh({ label, k, sortKey, dir, onClick, align }) {
  const active = sortKey === k;
  const arrow = !active ? "↕" : dir === "asc" ? "↑" : "↓";
  return <th className={`${align==="right"?"text-right":"text-left"} px-4 py-3 select-none`}>
    <button type="button" onClick={onClick} data-testid={`sort-${k}`} className={`inline-flex items-center gap-1 uppercase tracking-[0.05em] hover:text-zinc-900 transition-colors duration-150 ${active?"text-zinc-900":"text-zinc-500"}`}>
      {label}<span className={`font-mono text-[11px] ${active?"text-[#002FA7]":"text-zinc-400"}`}>{arrow}</span>
    </button>
  </th>;
}

function NewPIModal({ variant, extraHeaders, testIdPrefix, onClose, onCreated }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [company, setCompany] = useState("");
  const [invNo, setInvNo] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [invSuggestion, setInvSuggestion] = useState("");
  const [amtSuggestion, setAmtSuggestion] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannedNames = useRef(new Set());

  useEffect(() => {
    const f = files[files.length - 1];
    if (!f || scannedNames.current.has(f.name)) return;
    scannedNames.current.add(f.name);
    (async () => {
      setScanning(true);
      const { invoiceNumber, amount: amt } = await extractFromFile(f, extraHeaders);
      setScanning(false);
      if (invoiceNumber) {
        setInvSuggestion(invoiceNumber);
        setInvNo(cur => cur.trim() ? cur : invoiceNumber);
      }
      if (amt != null) {
        setAmtSuggestion(amt);
        setAmount(cur => (cur && Number(cur) > 0) ? cur : String(amt));
      }
    })();
  }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e?.preventDefault();
    if (!company.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const created = [];
      // At least one record is always created; if 0 files → 1 record with no file
      const rounds = files.length || 1;
      for (let i = 0; i < rounds; i++) {
        const fd = new FormData();
        fd.append("date", date);
        fd.append("company_name", company.trim());
        fd.append("inv_no", invNo.trim());
        fd.append("description", description.trim());
        fd.append("amount", String(amount || "0"));
        if (files[i]) fd.append("file", files[i]);
        const { data } = await api.post(`/purchase-invoices/${variant}`, fd, { headers: { "Content-Type": "multipart/form-data", ...extraHeaders }});
        created.push(data);
      }
      toast.success(created.length === 1 ? "Invoice added" : `${created.length} invoices added`);
      onCreated(created);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return <Modal title="New Purchase Invoice" testid={`new-${testIdPrefix}-modal`} onClose={onClose}>
    <form onSubmit={submit} className="p-5 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Date" required><input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid={`${testIdPrefix}-date-input`} className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
        <Fld label={variant==="protected"?"Inv / Statement #":"Invoice #"}>
          <input value={invNo} onChange={e=>setInvNo(e.target.value)} data-testid={`${testIdPrefix}-inv-input`} className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/>
          <InvSuggestion scanning={scanning} suggestion={invSuggestion} invNo={invNo} onUse={()=>{setInvNo(invSuggestion); setInvSuggestion("");}} onDismiss={()=>setInvSuggestion("")}/>
        </Fld>
      </div>
      <Fld label="Company Name" required><input value={company} onChange={e=>setCompany(e.target.value)} data-testid={`${testIdPrefix}-company-input`} className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="Amount (RM)">
        <input type="number" step="0.01" min="0" inputMode="decimal" value={amount} onChange={e=>setAmount(e.target.value)} data-testid={`${testIdPrefix}-amount-input`} placeholder="0.00" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/>
        <AmountSuggestion scanning={scanning} suggestion={amtSuggestion} current={amount} onUse={()=>{setAmount(String(amtSuggestion)); setAmtSuggestion(null);}} onDismiss={()=>setAmtSuggestion(null)}/>
      </Fld>
      <Fld label="Description"><textarea rows={2} value={description} onChange={e=>setDescription(e.target.value)} data-testid={`${testIdPrefix}-desc-input`} className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="Attachments (any file type — creates one invoice per file)">
        <MultiFilePicker files={files} onChange={setFiles} testid={`${testIdPrefix}-file`} hint="PDF, Excel, ZIP, CSV, Word, images…"/>
      </Fld>
      <ModalFooter onClose={onClose} saving={saving} submitTestId={`${testIdPrefix}-submit-btn`}/>
    </form>
  </Modal>;
}

function EditPIModal({ variant, extraHeaders, testIdPrefix, record, onClose, onSaved }) {
  const [date, setDate] = useState(record.date || "");
  const [company, setCompany] = useState(record.company_name || "");
  const [invNo, setInvNo] = useState(record.inv_no || "");
  const [amount, setAmount] = useState(record.amount != null ? String(record.amount) : "");
  const [description, setDescription] = useState(record.description || "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/purchase-invoices/${variant}/${record.id}`, {
        date, company_name: company.trim(), inv_no: invNo.trim(),
        description: description.trim(), amount: Number(amount || 0),
      }, { headers: extraHeaders });
      toast.success("Saved"); onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return <Modal title="Edit Invoice" testid={`edit-${testIdPrefix}-modal`} onClose={onClose}>
    <form onSubmit={submit} className="p-5 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Date"><input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid={`${testIdPrefix}-edit-date`} className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
        <Fld label="Invoice #"><input value={invNo} onChange={e=>setInvNo(e.target.value)} data-testid={`${testIdPrefix}-edit-inv`} className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
      </div>
      <Fld label="Company Name"><input value={company} onChange={e=>setCompany(e.target.value)} data-testid={`${testIdPrefix}-edit-company`} className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="Amount (RM)"><input type="number" step="0.01" min="0" inputMode="decimal" value={amount} onChange={e=>setAmount(e.target.value)} data-testid={`${testIdPrefix}-edit-amount`} placeholder="0.00" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="Description"><textarea rows={2} value={description} onChange={e=>setDescription(e.target.value)} data-testid={`${testIdPrefix}-edit-desc`} className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <ModalFooter onClose={onClose} saving={saving} submitTestId={`${testIdPrefix}-edit-save`}/>
    </form>
  </Modal>;
}
